import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import moment from 'moment'

import { fetcher } from 'repositories/BerthApi/berthApiClient'
import { TruckbookApiResponseError } from 'repositories/TruckbookApiResponseError'
import { getBookingMasterDataEndpoint } from 'repositories/BerthApi/getBookingMasterData/endpoint'
import type {
  BookingMasterData,
  GetBookingMasterDataResponse,
} from 'repositories/BerthApi/getBookingMasterData/type'
import { getTenantSettingsEndpoint } from 'repositories/BerthApi/getTenantSettings/endpoint'
import type { GetTenantSettingsResponse } from 'repositories/BerthApi/getTenantSettings/type'
import { getTenantShipments } from 'repositories/ShipmentApi/getTenantShipments'
import { bookingKeys, shipmentKeys, tenantKeys } from 'repositories/queryKeys'
import { isAuthError, isTenantSettingsLogoutError } from 'utils/apiErrorPredicates'
import { logout } from 'utils/logout'

import type { TenantShipment, DeliveryPerformanceFilter } from '../type'

export type { BookingMasterData }

const PER_PAGE = 50

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/

// デフォルト日付範囲: 過去3日（一昨日 00:00:00〜本日 23:59:59）
const getDefaultDateFrom = (): string => moment().subtract(2, 'days').format('YYYY-MM-DD')
const getDefaultDateTo = (): string => moment().format('YYYY-MM-DD')

const parseFilterFromSearch = (search: string): DeliveryPerformanceFilter => {
  const params = new URLSearchParams(search)
  const dateFrom = params.get('dateFrom') ?? ''
  const dateTo = params.get('dateTo') ?? ''
  return {
    dateFrom: YYYY_MM_DD.test(dateFrom) ? dateFrom : getDefaultDateFrom(),
    dateTo: YYYY_MM_DD.test(dateTo) ? dateTo : getDefaultDateTo(),
    driverPhone: params.get('phone') ?? '',
  }
}

const buildSearch = (filter: DeliveryPerformanceFilter): string => {
  const params = new URLSearchParams()
  if (filter.driverPhone) params.set('phone', filter.driverPhone)
  if (filter.dateFrom) params.set('dateFrom', filter.dateFrom)
  if (filter.dateTo) params.set('dateTo', filter.dateTo)
  const str = params.toString()
  return str ? `?${str}` : ''
}

export type UseDeliveryPerformanceListControllerArgs = {
  tenantId: number | null
}

export type DeliveryPerformanceListControllerReturn = {
  // 状態
  shipments: TenantShipment[]
  total: number
  totalPage: number
  perPage: number
  currentPage: number
  isLoading: boolean
  isTenantSettingsLoading: boolean
  isError: boolean
  isFeatureOff: boolean
  filter: DeliveryPerformanceFilter
  masterData: BookingMasterData | null
  // ハンドラ
  handleFilterChange: (patch: Partial<DeliveryPerformanceFilter>) => void
  handleSearch: () => void
  handlePageChange: (page: number) => void
  handleRowClick: (shipmentId: number) => void
}

export const useDeliveryPerformanceListController = ({
  tenantId,
}: UseDeliveryPerformanceListControllerArgs): DeliveryPerformanceListControllerReturn => {
  const location = useLocation()
  const history = useHistory()

  // 入力中の filter（フォーム）と、検索で確定した appliedFilter（取得に使う）を分ける。
  // 初期値は URL クエリ文字列から復元する。クエリなし or 無効値はデフォルト日付範囲を使う。
  const [filter, setFilter] = useState<DeliveryPerformanceFilter>(() =>
    parseFilterFromSearch(location.search)
  )
  const [appliedFilter, setAppliedFilter] = useState<DeliveryPerformanceFilter>(() =>
    parseFilterFromSearch(location.search)
  )
  const [currentPage, setCurrentPage] = useState(1)

  // 倉庫 / テナント変更時はページを 1 に戻す（一覧はテナント切替で再取得＝key 変化で自動）
  useEffect(() => {
    setCurrentPage(1)
  }, [tenantId])

  const trimmedPhone = appliedFilter.driverPhone.replace(/[-\s]/g, '')
  const queryParams = {
    driverPhoneNumber: trimmedPhone || undefined,
    createdAtFrom: appliedFilter.dateFrom ? `${appliedFilter.dateFrom}T00:00:00` : undefined,
    createdAtTo: appliedFilter.dateTo ? `${appliedFilter.dateTo}T23:59:59` : undefined,
    page: currentPage,
    perPage: PER_PAGE,
  }

  // 区分 ON/OFF を事前に取得。OFF なら shipments リクエストを抑制する
  const {
    data: tenantSettingsResponse,
    isLoading: isTenantSettingsLoading,
    error: tenantSettingsError,
  } = useQuery<GetTenantSettingsResponse>({
    queryKey: tenantKeys.settingsByTenant(String(tenantId ?? '')),
    queryFn: () => fetcher(getTenantSettingsEndpoint(String(tenantId!))),
    enabled: tenantId != null,
  })
  const useDeliveryTenant = tenantSettingsResponse?.data?.useDeliveryTenant ?? null

  const { data: masterDataResponse } = useQuery<GetBookingMasterDataResponse>({
    queryKey: bookingKeys.masterDataByTenant(String(tenantId ?? '')),
    queryFn: () => fetcher(getBookingMasterDataEndpoint(String(tenantId!))),
    enabled: tenantId != null,
  })
  const masterData = masterDataResponse?.data ?? null

  const {
    data,
    isLoading: isShipmentsLoading,
    isError: isQueryError,
    error,
  } = useQuery({
    queryKey: shipmentKeys.list(tenantId ?? 0, queryParams),
    // tenantSettings が区分ON を確認してからリクエストする
    enabled: tenantId != null && useDeliveryTenant === true,
    // tenantId は enabled ガードにより非 null。camel→snake 変換は repository が担う
    queryFn: () => getTenantShipments({ tenantId: tenantId as number, ...queryParams }),
  })

  // 401/403 (認証切れ・アクセス権限なし): ログアウトする。
  // 権限を外された状態でリロード時に発生する（be PR #4982 の認可チェック）。
  // tenantSettings は加えて 404 + code "917"/"2000"（TB-1882: 非所属/権限なし/テナント不存在/
  // ユーザー不存在）でもログアウトする。詳細は utils/apiErrorPredicates を参照。
  // TODO(interceptor): berthApiClient に createTruckbookApiErrorInterceptor を配線後は
  // この 401/403 ハンドリングを onError コールバックに集約できる。
  // 配線手順は .claude/rules/error-handling.md を参照。
  const isAuthErrorShipments = isAuthError(error)
  const isLogoutErrorTenantSettings = isTenantSettingsLogoutError(tenantSettingsError)
  useEffect(() => {
    if (isAuthErrorShipments || isLogoutErrorTenantSettings) {
      logout()
    }
  }, [isAuthErrorShipments, isLogoutErrorTenantSettings])

  // 区分 OFF: テナント設定が取得済みで use_delivery_tenant=false のとき。
  // 422 フォールバック: 設定が ON でも BE が 422 を返した場合（競合等）にも対応する。
  const is422 =
    (error instanceof TruckbookApiResponseError && error.httpStatus === 422) ||
    (isAxiosError(error) && error.response?.status === 422)
  const isFeatureOff =
    (tenantId != null && !isTenantSettingsLoading && useDeliveryTenant === false) || is422
  const isError = isQueryError && !is422 && !isAuthErrorShipments

  const shipments = data?.data ?? []
  const total = data?.total ?? 0
  const totalPage = data?.totalPage ?? 0

  const handleFilterChange = useCallback((patch: Partial<DeliveryPerformanceFilter>) => {
    setFilter((prev) => ({ ...prev, ...patch }))
  }, [])

  const handleSearch = useCallback(() => {
    setAppliedFilter(filter)
    setCurrentPage(1)
    history.replace({ search: buildSearch(filter) })
  }, [filter, history])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handleRowClick = useCallback(
    (shipmentId: number) => {
      // 詳細から「戻る」で検索条件付きの一覧 URL に復帰できるよう、遷移元が一覧である印を state に持たせる
      history.push(`/delivery-performances/${shipmentId}`, { fromList: true })
    },
    [history]
  )

  return {
    shipments,
    total,
    totalPage,
    perPage: PER_PAGE,
    currentPage,
    isTenantSettingsLoading: tenantId != null && isTenantSettingsLoading,
    isLoading: tenantId != null && (isTenantSettingsLoading || isShipmentsLoading),
    isError,
    isFeatureOff,
    filter,
    masterData,
    handleFilterChange,
    handleSearch,
    handlePageChange,
    handleRowClick,
  }
}
